import { Link } from "@tanstack/react-router";
import { waitLabel, type QueueItem } from "@/app/reviewQueries";
import { Avatar } from "@/app/nav";
import { TONE_CLASS } from "./Buckets";
import { checksLabel, checksTone, cohortLabel, flagLabel, iterLabel, rowTone, waitTone } from "./model";

const HEAD = ["", "студент", "задание", "ждёт", "автопроверки", "итерация", "статус"];

/** Чип флага «предложить созвон» (правило 10 CLAUDE.md): причина написана рядом. */
export function FlagChip({ flag, compact = false }: { flag: NonNullable<QueueItem["flag"]>; compact?: boolean }) {
  return (
    <span
      data-testid="flag"
      title={flagLabel(flag)}
      className={`inline-flex max-w-full items-center gap-1.5 rounded-sm border border-warn bg-warnsoft px-2 py-[3px] font-mono text-warn ${compact ? "text-[10.5px]" : "text-[11px]"}`}
    >
      <span aria-hidden>↗</span>
      <span className="truncate">предложить созвон · {flag.count}× {flag.reason.label}</span>
    </span>
  );
}

export function StatusPill({ late }: { late: boolean }) {
  return (
    <span data-testid="row-status" className={`inline-flex items-center gap-1.5 rounded-sm px-2 py-[3px] font-mono text-[11px] ${late ? "bg-errsoft text-err" : "bg-warnsoft text-warn"}`}>
      <span aria-hidden className="size-1.5 rounded-full bg-current" />
      {late ? "просрочено" : "ждёт"}
    </span>
  );
}

/** Строки очереди: клик выбирает сдачу для карточки справа; имя ведёт в M3. */
export function Rows({ rows, selectedId, onSelect }: { rows: QueueItem[]; selectedId?: string; onSelect: (id: string) => void }) {
  return (
    <table className="w-full border-collapse" data-testid="queue-table">
      <thead>
        <tr className="bg-bg2">
          {HEAD.map((h, i) => (
            <th key={i} scope="col" className="px-2.5 py-2.5 text-left font-mono text-[10.5px] font-normal uppercase tracking-[.06em] text-mut first:pl-[22px] last:pr-[22px]">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const on = r.submission_id === selectedId;
          return (
            <tr
              key={r.submission_id}
              data-testid="queue-row"
              data-id={r.submission_id}
              aria-selected={on}
              tabIndex={0}
              onClick={() => onSelect(r.submission_id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelect(r.submission_id);
                }
              }}
              className={`cursor-pointer border-b border-bd align-top ${on ? "bg-card" : "hover:bg-card2/40"}`}
              style={{ boxShadow: on ? "inset 2px 0 0 var(--acc)" : undefined }}
            >
              <td className="py-3 pl-[22px] pr-1">
                <span aria-hidden className="mt-1.5 block size-2 rounded-full" style={{ background: `var(--${rowTone(r)})` }} />
              </td>
              <td className="px-2.5 py-3">
                <span className="flex items-start gap-2.5">
                  <Avatar name={r.student.name} />
                  <span className="flex min-w-0 flex-col">
                    <Link
                      to="/mentor/student/$userId"
                      params={{ userId: r.student.id }}
                      onClick={(e) => e.stopPropagation()}
                      className="truncate text-[13px] text-ink no-underline hover:text-acc hover:underline"
                    >
                      {r.student.name}
                    </Link>
                    <span className={`font-mono text-[10.5px] ${r.cohort ? "text-mut" : "text-ink2"}`}>{cohortLabel(r)}</span>
                  </span>
                </span>
              </td>
              <td className="px-2.5 py-3">
                <span className="block font-mono text-[12px] text-ink2">{r.assignment}</span>
                {r.flag && (
                  <span className="mt-1.5 block">
                    <FlagChip flag={r.flag} compact />
                  </span>
                )}
              </td>
              <td className={`px-2.5 py-3 font-mono text-[12px] ${TONE_CLASS[waitTone(r)]}`} data-testid="row-wait">
                {waitLabel(r.wait_hours)}
              </td>
              <td className={`px-2.5 py-3 font-mono text-[12px] ${TONE_CLASS[checksTone(r)]}`} data-testid="row-checks">
                {checksLabel(r)}
              </td>
              <td className="px-2.5 py-3 font-mono text-[12px] text-mut">{iterLabel(r)}</td>
              <td className="py-3 pl-2.5 pr-[22px]">
                <StatusPill late={r.late} />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
